import React, { useEffect, useRef, useState } from "react";

// NYSE full-closure holidays. Needs a yearly refresh — floating holidays
// (MLK, Presidents Day, Good Friday, Memorial Day, Labor Day, Thanksgiving)
// shift date each year. Early-close days (e.g. day after Thanksgiving) are
// not modeled — this only tracks full closures.
const NYSE_HOLIDAYS = [
  "2026-01-01", // New Year's Day
  "2026-01-19", // MLK Day
  "2026-02-16", // Presidents Day
  "2026-04-03", // Good Friday
  "2026-05-25", // Memorial Day
  "2026-06-19", // Juneteenth
  "2026-07-03", // Independence Day (observed)
  "2026-09-07", // Labor Day
  "2026-11-26", // Thanksgiving
  "2026-12-25", // Christmas
];

const pad2 = (n) => String(n).padStart(2, "0");

function getEasternParts(date) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    weekday: "short",
  });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  return {
    year: +parts.year, month: +parts.month, day: +parts.day,
    hour: +parts.hour, minute: +parts.minute, second: +parts.second,
    weekday: parts.weekday,
  };
}

function nyOffsetMinutes(date) {
  const fmt = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", timeZoneName: "shortOffset" });
  const tzPart = fmt.formatToParts(date).find((p) => p.type === "timeZoneName")?.value || "GMT-5";
  const match = tzPart.match(/GMT([+-]\d+)/);
  return match ? parseInt(match[1], 10) * 60 : -300;
}

// Build a Date for a specific wall-clock time in America/New_York on (y, m, d).
function nyDateTime(y, m, d, hh, mm) {
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const offsetMin = nyOffsetMinutes(guess);
  return new Date(Date.UTC(y, m - 1, d, hh, mm) - offsetMin * 60000);
}

function isTradingDay(y, m, d) {
  const dateStr = `${y}-${pad2(m)}-${pad2(d)}`;
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun..6=Sat
  return weekday !== 0 && weekday !== 6 && !NYSE_HOLIDAYS.includes(dateStr);
}

function findNextTradingDay(y, m, d) {
  const dt = new Date(Date.UTC(y, m - 1, d));
  while (!isTradingDay(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate())) {
    dt.setUTCDate(dt.getUTCDate() + 1);
  }
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function computeMarketStatus(now) {
  const p = getEasternParts(now);
  const { year: y, month: m, day: d } = p;
  const todayIsTradingDay = isTradingDay(y, m, d);
  const todayOpen = nyDateTime(y, m, d, 9, 30);
  const todayClose = nyDateTime(y, m, d, 16, 0);

  const isOpen = todayIsTradingDay && now >= todayOpen && now < todayClose;

  let nextOpen;
  if (todayIsTradingDay && now < todayOpen) {
    nextOpen = todayOpen;
  } else {
    const tomorrow = new Date(Date.UTC(y, m - 1, d + 1));
    const nd = findNextTradingDay(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth() + 1, tomorrow.getUTCDate());
    nextOpen = nyDateTime(nd.y, nd.m, nd.d, 9, 30);
  }
  const nextClose = isOpen ? todayClose : new Date(nextOpen.getTime() + 6.5 * 3600000);

  return { p, isOpen, todayIsTradingDay, todayOpen, todayClose, nextOpen, nextClose };
}

function formatCountdown(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (days > 0) return `${days}d ${pad2(hours)}:${pad2(mins)}:${pad2(secs)}`;
  return `${pad2(hours)}:${pad2(mins)}:${pad2(secs)}`;
}

function formatEtClock(p) {
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const ampm = p.hour < 12 ? "AM" : "PM";
  return `${h12}:${pad2(p.minute)}:${pad2(p.second)} ${ampm} ET`;
}

function formatEtDate(p) {
  const dt = nyDateTime(p.year, p.month, p.day, 12, 0);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric", year: "numeric",
  }).format(dt);
}

export default function MarketClock() {
  const [now, setNow] = useState(() => new Date());
  const [open, setOpen] = useState(false);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [toast, setToast] = useState(null);
  const prevIsOpenRef = useRef(null);
  const containerRef = useRef(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const status = computeMarketStatus(now);

  useEffect(() => {
    if (prevIsOpenRef.current === null) {
      prevIsOpenRef.current = status.isOpen;
      return;
    }
    if (prevIsOpenRef.current === status.isOpen) return;
    prevIsOpenRef.current = status.isOpen;

    const msg = status.isOpen ? "Market is now open" : "Market is now closed";
    setToast(msg);
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      new Notification("SwingTrader Pro", { body: msg });
    }
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 8000);
  }, [status.isOpen]);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  const requestNotifications = () => {
    if (typeof Notification === "undefined") return;
    Notification.requestPermission().then(setNotifPermission);
  };

  const countdownMs = status.isOpen
    ? status.nextClose.getTime() - now.getTime()
    : status.nextOpen.getTime() - now.getTime();

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
        style={{ backgroundColor: open ? "var(--bg-hover)" : "var(--bg-card)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
      >
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: status.isOpen ? "var(--gain-green)" : "var(--loss-red)" }}
        />
        <span>{status.isOpen ? "Open" : "Closed"}</span>
        <span style={{ color: "var(--text-muted)" }}>
          {status.isOpen ? "closes in" : "opens in"} {formatCountdown(countdownMs)}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 rounded-lg overflow-hidden z-50 p-4"
          style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)" }}
        >
          <div className="text-sm font-semibold mb-1" style={{ color: "var(--text-primary)" }}>{formatEtDate(status.p)}</div>
          <div className="text-lg font-bold mb-3" style={{ color: "var(--text-primary)" }}>{formatEtClock(status.p)}</div>

          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: status.isOpen ? "var(--gain-green)" : "var(--loss-red)" }} />
            <span className="text-sm font-medium" style={{ color: status.isOpen ? "var(--text-green-soft)" : "var(--text-red-soft)" }}>
              {status.isOpen ? "Market Open" : "Market Closed"}
            </span>
          </div>

          <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>Regular session</div>
          <div className="text-sm mb-3" style={{ color: "var(--text-primary)" }}>9:30 AM – 4:00 PM ET</div>

          <div
            className="rounded-lg px-3 py-2 mb-3 text-sm"
            style={{ backgroundColor: "var(--bg-nested)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
          >
            {status.isOpen ? "Closes in " : "Opens in "}
            <span className="font-mono font-semibold">{formatCountdown(countdownMs)}</span>
          </div>

          {notifPermission === "granted" ? (
            <div className="text-xs" style={{ color: "var(--text-green-soft)" }}>Alerts enabled for open/close</div>
          ) : notifPermission === "denied" ? (
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
              Browser notifications blocked — enable them in your browser's site settings to get alerts.
            </div>
          ) : notifPermission === "unsupported" ? null : (
            <button
              onClick={requestNotifications}
              className="w-full text-xs py-2 rounded-lg font-medium transition-colors"
              style={{ backgroundColor: "var(--accent-blue)", color: "#fff" }}
            >
              Enable open/close alerts
            </button>
          )}
        </div>
      )}

      {toast && (
        <div
          className="fixed top-4 right-4 z-50 rounded-lg px-4 py-3 text-sm shadow-lg flex items-center gap-2"
          style={{ backgroundColor: "var(--bg-card)", border: `1px solid ${status.isOpen ? "var(--gain-green)" : "var(--loss-red)"}`, color: "var(--text-primary)" }}
        >
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: status.isOpen ? "var(--gain-green)" : "var(--loss-red)" }} />
          {toast}
        </div>
      )}
    </div>
  );
}
